import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';

import { VipCrownPng } from '../../../assets/bonuses';
import { selectProfile } from '../../../store/auth/authReducer';
import {
	peekVipTicketBalance,
	refreshVipTicketBalance,
	useVipTicketPlayerId,
} from '../../../utils/hooks/useVipTicketBalance';
import RedesignConfirmModal from '../Modals/RedesignConfirmModal';
import ArcadeGameCard from './ArcadeGameCard';
import { BONUS_FONT, bonusColors, RADIUS_SM } from './bonusesTheme';
import { ArcadeGame } from './bonusGamesCatalog';
import BonusSection from './BonusSection';

// Figma "VIP Bonus Games" (7019:79903): a 370x370 plate holding the crown heading and, in
// "VIP games / 4-column grid" (x 8, y 58, 354x300), two rows of 84x147 compact cards 90 apart -
// four across, then the three coming-soon games from the left.
export const VIP_SECTION_DESIGN_WIDTH = 370;
export const VIP_SECTION_DESIGN_HEIGHT = 370;
/** The plate cut after the first row: the row's 147-high cards from 58, with 12 under them. */
export const VIP_SECTION_SINGLE_ROW_DESIGN_HEIGHT = 217;

const CROWN = { x: 8, y: 14, size: 36 };
const TITLE = { x: 52, y: 13, fontSize: 16, tracking: -0.28 };
// The badge sits 6 after the title in the file's row.
const BADGE = { gap: 6, width: 28, height: 17, fontSize: 10 };
const ELIGIBILITY = { x: 52, y: 37, fontSize: 10 };
const ROW_TOP = [58, 211];
const ROW_LEFT = 8;
const CARD_PITCH = 90;

type Props = {
	/** Row one - the four VIP games. */
	games: ArcadeGame[];
	/** Row two, from the left under it: the three coming-soon games. */
	secondRow: ArcadeGame[];
	width: number;
	/** Just the four games, without the second row. */
	singleRow?: boolean;
	/** PLAY on a card that has an `arcadeGameType`; cards without one stay inert. */
	onPlay?: (game: ArcadeGame) => void;
};

/** What a PLAY on a VIP game asks first: a VIP plays, anyone else spends a ticket or is told how. */
type Gate = { kind: 'ticket'; game: ArcadeGame } | { kind: 'locked' };

export const VipBonusGames = ({ games, secondRow, width, singleRow = false, onPlay }: Props) => {
	const { t } = useTranslation();
	const scale = width / VIP_SECTION_DESIGN_WIDTH;
	const styles = useMemo(() => createStyles(scale), [scale]);
	const playerId = useVipTicketPlayerId();
	const isVip = !!useSelector(selectProfile)?.isVip;
	const [gate, setGate] = useState<Gate | null>(null);

	// Nothing is spent on this side: the game opens on its own entry controls, where a non-VIP
	// holding tickets has the ticket as its only currency, and the backend takes the ticket as
	// that round's entry. A guest goes straight to the host, which sends them to log in.
	// The gate opens on the balance the badge already read, so it does not wait on the wallet; the
	// read behind it keeps the next PLAY current. Only a first PLAY with nothing read yet waits.
	const requestPlay = async (game: ArcadeGame) => {
		if (isVip || playerId == null) {
			onPlay?.(game);
			return;
		}
		const refresh = refreshVipTicketBalance(playerId);
		const tickets = peekVipTicketBalance(playerId) ?? (await refresh) ?? 0;
		setGate(tickets > 0 ? { kind: 'ticket', game } : { kind: 'locked' });
	};

	const closeGate = () => setGate(null);

	const renderRow = (row: ArcadeGame[], rowIndex: number) =>
		row.map((game, index) => (
			<View
				key={`${rowIndex}-${game.key}`}
				style={[
					styles.cell,
					{
						left: (ROW_LEFT + index * CARD_PITCH) * scale,
						top: ROW_TOP[rowIndex] * scale,
					},
				]}
			>
				<ArcadeGameCard
					game={game}
					variant='compact'
					scale={scale}
					onPress={game.arcadeGameType && onPlay ? () => requestPlay(game) : undefined}
					playLabel={game.arcadeGameType ? 'PLAY' : 'SOON'}
				/>
			</View>
		));

	return (
		<BonusSection
			designWidth={VIP_SECTION_DESIGN_WIDTH}
			designHeight={singleRow ? VIP_SECTION_SINGLE_ROW_DESIGN_HEIGHT : VIP_SECTION_DESIGN_HEIGHT}
			scale={scale}
		>
			<Image source={VipCrownPng} style={styles.crown} resizeMode='contain' />

			<View style={styles.titleRow}>
				<Text numberOfLines={1} style={styles.title}>
					{t('T_SITE_VIP_ARCADE_TITLE', { defaultValue: 'VIP ARCADE' })}
				</Text>

				<View style={styles.badge}>
					<Text numberOfLines={1} style={styles.badgeLabel}>
						{t('T_SITE_BADGE_NEW', { defaultValue: 'NEW' })}
					</Text>
				</View>
			</View>

			<Text numberOfLines={1} style={styles.eligibility}>
				{t('T_SITE_VIP_BONUS_GAMES_SUBTITLE', {
					defaultValue: 'Exclusive games for VIP Club members!',
				})}
			</Text>

			{renderRow(games, 0)}
			{singleRow ? null : renderRow(secondRow, 1)}

			{gate?.kind === 'ticket' ? (
				<RedesignConfirmModal
					message={t('T_SITE_VIP_ARCADE_TICKET_CONFIRM', {
						defaultValue: 'Play this game for 1 VIP Ticket?',
					})}
					cancelLabel={t('T_SITE_NO', { defaultValue: 'No' })}
					confirmLabel={t('T_SITE_YES', { defaultValue: 'Yes' })}
					onCancel={closeGate}
					onConfirm={() => {
						closeGate();
						onPlay?.(gate.game);
					}}
					cancelTestId='cta-vip-arcade-ticket-no'
					confirmTestId='cta-vip-arcade-ticket-yes'
				/>
			) : null}

			{gate?.kind === 'locked' ? (
				<RedesignConfirmModal
					message={t('T_SITE_VIP_ARCADE_LOCKED', {
						defaultValue: 'In order to play this game you need to be VIP or use VIP Ticket',
					})}
					confirmLabel={t('T_OK', { defaultValue: 'OK' })}
					onCancel={closeGate}
					onConfirm={closeGate}
					confirmTestId='cta-vip-arcade-locked-ok'
				/>
			) : null}
		</BonusSection>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		crown: {
			position: 'absolute',
			left: CROWN.x * scale,
			top: CROWN.y * scale,
			width: CROWN.size * scale,
			height: CROWN.size * scale,
		},
		titleRow: {
			position: 'absolute',
			left: TITLE.x * scale,
			top: TITLE.y * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: BADGE.gap * scale,
		},
		// Figma "Numeric/Balance": Sofia Sans Bold 16 / 16, text/accent.
		title: {
			color: bonusColors.accent,
			fontFamily: BONUS_FONT,
			fontSize: TITLE.fontSize * scale,
			fontWeight: '700',
			letterSpacing: TITLE.tracking * scale,
			lineHeight: TITLE.fontSize * 1.2 * scale,
		},
		badge: {
			width: BADGE.width * scale,
			height: BADGE.height * scale,
			borderRadius: RADIUS_SM * scale,
			borderWidth: Math.max(0.5, 0.5 * scale),
			borderColor: bonusColors.gold300,
			backgroundColor: bonusColors.surfaceOverlay,
			alignItems: 'center',
			justifyContent: 'center',
		},
		// Figma "DS/Label/S": Sofia Sans SemiBold 10, text/primary.
		badgeLabel: {
			color: bonusColors.primary,
			fontFamily: BONUS_FONT,
			fontSize: BADGE.fontSize * scale,
			fontWeight: '600',
			lineHeight: BADGE.fontSize * 1.2 * scale,
		},
		// Figma "Caption/Tiny": Sofia Sans Medium 10, text/primary, tracking 0.0195.
		eligibility: {
			position: 'absolute',
			left: ELIGIBILITY.x * scale,
			top: ELIGIBILITY.y * scale,
			color: bonusColors.primary,
			fontFamily: BONUS_FONT,
			fontSize: ELIGIBILITY.fontSize * scale,
			fontWeight: '500',
			letterSpacing: 0.0195 * scale,
			lineHeight: ELIGIBILITY.fontSize * scale,
		},
		cell: {
			position: 'absolute',
		},
	});

export default VipBonusGames;
